// PrivacyNoticePage.jsx
import React, { useState, useEffect, useMemo } from "react";

const PrivacyNoticePage = () => {
  const [activeSection, setActiveSection] = useState("introduction");

  const sections = useMemo(() => [
    { id: "introduction", title: "Introduction" },
    { id: "access", title: "1. Access to the Website" },
    { id: "information", title: "2. Information We Collect" },
    { id: "cookies", title: "3. Use of Cookies" },
    { id: "use", title: "4. Use of Information" },
    { id: "third-party", title: "5. Third-Party Tools" },
    { id: "external", title: "6. External Links" },
    { id: "updates", title: "7. Updates to This Notice" },
    { id: "contact", title: "8. Contact" },
  ], []);

  // Handle scroll spy to highlight active section
  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY + 120;
      
      for (let i = sections.length - 1; i >= 0; i--) {
        const section = document.getElementById(sections[i].id);
        if (section) {
          const offsetTop = section.offsetTop;
          if (scrollPosition >= offsetTop) {
            setActiveSection(sections[i].id);
            break;
          }
        }
      }
    };

    window.addEventListener("scroll", handleScroll);
    handleScroll();
    
    return () => window.removeEventListener("scroll", handleScroll);
  }, [sections]);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      const navbarHeight = 80;
      const elementPosition = element.offsetTop - navbarHeight;
      window.scrollTo({
        top: elementPosition,
        behavior: "smooth"
      });
    }
    setActiveSection(sectionId);
  };

  return (
    <div className="privacy-page-container">
      {/* Sidebar Navigation - Fixed on left */}
      <aside className="privacy-sidebar">
        <div className="sidebar-sticky">
          <h3 className="sidebar-title">Privacy Notice</h3>
          <nav className="sidebar-nav">
            {sections.map((section) => (
              <button
                key={section.id}
                onClick={() => scrollToSection(section.id)}
                className={`sidebar-link ${activeSection === section.id ? "active" : ""}`}
              >
                {section.title}
              </button>
            ))}
          </nav>
        </div>
      </aside>

      {/* Main Content - Scrolls on right */}
      <main className="privacy-main-content">
        <div className="museum-wrap">
          {/* Introduction Section */}
          <div id="introduction" className="privacy-section">
            <h1 className="privacy-heading">Privacy Policy
              <span className="heading-highlight"> </span>
            </h1>
            <p className="section-text">
              This website is operated by Morphis and is intended to provide
              information about our capital markets advisory and related
              services to a business audience, including founders, private
              equity firms, and corporate stakeholders.
            </p>
          </div>

          {/* Section 1 */}
          <div id="access" className="privacy-section">
            <div className="section-divider"></div>
            <h2 className="section-subheading">1. Access to the Website</h2>
            <p className="section-text">
              You may access and browse this website freely without providing
              any personal information. The website is designed purely for
              informational purposes, and no registration, login, or submission
              of personal data is required to view its content.
            </p>
          </div>

          {/* Section 2 */}
          <div id="information" className="privacy-section">
            <div className="section-divider"></div>
            <h2 className="section-subheading">2. Information We Collect</h2>
            <p className="section-text">
              We do not collect personally identifiable information from
              visitors to this website.
            </p>
            <p className="section-text">
              However, certain limited, non-personal information may be
              collected automatically when you browse the site. This may
              include:
            </p>
            <ul className="section-list">
              <li>General device and browser information</li>
              <li>Pages visited and time spent on the site</li>
              <li>Aggregated usage data to understand overall traffic patterns</li>
            </ul>
            <p className="section-text">
              This information does not identify you personally and is used only
              to maintain and improve the website.
            </p>
          </div>

          {/* Section 3 */}
          <div id="cookies" className="privacy-section">
            <div className="section-divider"></div>
            <h2 className="section-subheading">3. Use of Cookies</h2>
            <p className="section-text">
              The website may use basic cookies or similar technologies to
              support functionality and understand general usage trends.
            </p>
            <p className="section-text">
              You may choose to disable cookies through your browser settings.
              Please note that some elements of the site may not function
              optimally if cookies are disabled.
            </p>
          </div>

          {/* Section 4 */}
          <div id="use" className="privacy-section">
            <div className="section-divider"></div>
            <h2 className="section-subheading">4. Use of Information</h2>
            <p className="section-text">
              Any non-personal information collected is used solely for:
            </p>
            <ul className="section-list">
              <li>Ensuring the website functions effectively</li>
              <li>Monitoring and improving user experience</li>
              <li>Maintaining security and performance</li>
            </ul>
            <p className="section-text">
              We do not use this information for marketing or profiling
              individual users.
            </p>
          </div>

          {/* Section 5 */}
          <div id="third-party" className="privacy-section">
            <div className="section-divider"></div>
            <h2 className="section-subheading">5. Third-Party Tools</h2>
            <p className="section-text">
              We may use standard third-party tools (such as website analytics
              services) to help us understand overall site usage. These tools
              process only aggregated, non-personal data.
            </p>
            <p className="section-text">
              We do not sell, share, or disclose user data for commercial
              purposes.
            </p>
          </div>

          {/* Section 6 */}
          <div id="external" className="privacy-section">
            <div className="section-divider"></div>
            <h2 className="section-subheading">6. External Links</h2>
            <p className="section-text">
              This website may contain links to external websites for
              convenience. We are not responsible for the content or privacy
              practices of those websites.
            </p>
          </div>

          {/* Section 7 */}
          <div id="updates" className="privacy-section">
            <div className="section-divider"></div>
            <h2 className="section-subheading">7. Updates to This Notice</h2>
            <p className="section-text">
              This notice may be updated from time to time. Any changes will be
              reflected on this page.
            </p>
          </div>

          {/* Section 8 - Contact */}
          <div id="contact" className="privacy-section">
            <div className="section-divider"></div>
            <h2 className="section-subheading">8. Contact</h2>
            <p className="section-text">
              For any questions regarding this website or this notice, please
              contact:
            </p>
            <div className="contact-info">
              <p className="contact-name">Morphis</p>
              <a
                href="mailto:vinni.rajani@morphis.co.in"
                className="contact-email"
              >
                 vinni.rajani@morphis.co.in
              </a>
            </div>
          </div>
        </div>
      </main>

      <style>{`
        .privacy-page-container {
          display: flex;
          min-height: 100vh;
          background: var(--bg-main, #fffdf6);
          padding-top: 80px;
        }

        /* Sidebar Styles */
        .privacy-sidebar {
          width: 260px;
          position: fixed;
          left: 0;
          top: 80px;
          height: calc(100vh - 80px);
          background: var(--bg-main, #fffdf6);
          border-right: 1px solid var(--border-light, #e6e0d4);
          overflow-y: auto;
          z-index: 99;
        }

        .sidebar-sticky {
          padding: 32px 20px;
          position: sticky;
          top: 0;
        }

        .sidebar-title {
          font-family: var(--font-heading);
          font-size: 1.25rem;
          font-weight: 400;
          color: var(--color-orange);
          margin-bottom: 28px;
          display: inline-block;
        }

        .sidebar-nav {
          display: flex;
          flex-direction: column;
          gap: 4px;
        }

        .sidebar-link {
          font-family: var(--font-body);
          font-size: 0.85rem;
          color: var(--text-sub, #2d2d2d);
          background: none;
          border: none;
          text-align: left;
          padding: 8px 12px;
          cursor: pointer;
          transition: all 0.2s ease;
          border-radius: 4px;
          font-weight: 400;
          line-height: 1.4;
        }

        .sidebar-link:hover {
          color: var(--color-orange, #E87722);
        }

        .sidebar-link.active {
          color: var(--color-orange, #E87722);
          font-weight: 400;
        }

        /* Main Content Styles */
        .privacy-main-content {
          flex: 1;
          margin-left: 260px;
          min-height: calc(100vh - 80px);
          background: var(--bg-main, #fffdf6);
        }

        .privacy-main-content .museum-wrap {
          margin: 0 auto;
          padding: 0 48px 60px 48px;
        }

        .privacy-section {
          margin-bottom: 32px;
          scroll-margin-top: 100px;
        }

        /* Orange Title Styling */
        .privacy-heading {
          font-family: var(--font-heading);
          font-size: 2.2rem;
          line-height: 1.2;
          margin-bottom: 24px;
          font-weight: 400;
        }

        .heading-text {
          color: var(--color-orange);
        }

        .heading-highlight {
          color: var(--color-orange, #E87722);
        }

        /* Alternative: Full orange title */
        .privacy-heading-full-orange {
          font-family: var(--font-heading);
          font-size: 2.2rem;
          line-height: 1.2;
          margin-bottom: 24px;
          color: var(--color-orange, #E87722);
          font-weight: 400;
        }

        .section-subheading {
          font-family: var(--font-heading);
          font-size: 1.25rem;
          margin-bottom: 16px;
          font-weight: 400;
        }

        .privacy-section:hover .section-subheading{
          color: var(--color-orange, #E87722);}

        .section-text {
          font-family: var(--font-body);
          font-size: 0.9rem;
          color: var(--text-sub, #2d2d2d);
          line-height: 1.6;
          margin-bottom: 12px;
        }

        .section-list {
          font-family: var(--font-body);
          font-size: 0.9rem;
          color: var(--text-sub, #2d2d2d);
          line-height: 1.6;
          margin-left: 1.25rem;
          margin-bottom: 12px;
          margin-top: 8px;
        }

        .section-list li {
          margin-bottom: 6px;
        }

        .contact-info {
          margin-top: 12px;
        }

        .contact-name {
          font-family: var(--font-body);
          font-size: 0.9rem;
          font-weight: 400;
          color: var(--color-dark, #1a1a1a);
          margin-bottom: 4px;
        }

        .contact-email {
          font-family: var(--font-body);
          font-size: 0.9rem;
          color: var(--color-orange, #E87722);
          text-decoration: none;
          transition: color 0.2s ease;
        }

        .contact-email:hover {
          color: var(--color-orange, #E87722);
          text-decoration: underline;
        }

        /* Mobile Responsive - Hide sidebar navigation */
        @media (max-width: 768px) {
          .privacy-page-container {
            padding-top: 70px;
          }
          
          .privacy-sidebar {
            display: none;
          }

          .privacy-main-content {
            margin-left: 0;
            width: 100%;
          }

          .privacy-main-content .museum-wrap {
            padding: 0 24px 48px 24px;
          }

          .privacy-section {
            scroll-margin-top: 80px;
          }
        }

        /* Small mobile devices */
        @media (max-width: 480px) {
          .privacy-heading {
            font-size: 1.6rem;
          }
          
          .section-subheading {
            font-size: 1.1rem;
          }
          
          .section-text,
          .section-list,
          .contact-name,
          .contact-email {
            font-size: 0.85rem;
          }

          .privacy-main-content .museum-wrap {
            padding: 0 20px 40px 20px;
          }
        }

        /* Scrollbar Styling for Sidebar */
        .privacy-sidebar::-webkit-scrollbar {
          width: 3px;
        }

        .privacy-sidebar::-webkit-scrollbar-track {
          background: var(--border-light, #e6e0d4);
        }

        .privacy-sidebar::-webkit-scrollbar-thumb {
          background: var(--color-orange, #E87722);
          border-radius: 3px;
        }
      `}</style>
    </div>
  );
};

export default PrivacyNoticePage;